import {
  CalendarOutlined,
  CheckSquareOutlined,
  LeftOutlined,
  RightOutlined,
  EditOutlined,
  HomeOutlined,
  PieChartOutlined,
  PlusOutlined,
  PrinterOutlined,
  SaveOutlined,
} from "@ant-design/icons";
import {
  Button,
  DatePicker,
  FloatButton,
  Form,
  Input,
  InputNumber,
  Modal,
  Popconfirm,
  Segmented,
  Select,
  message,
} from "antd";
import dayjs from "dayjs";
import {
  collection,
  doc,
  onSnapshot,
  orderBy,
  query,
  setDoc,
  updateDoc,
  where,
} from "firebase/firestore";
import { Moment } from "moment";
import { nanoid } from "nanoid";
import pdfMake from "pdfmake/build/pdfmake";
import pdfFonts from "pdfmake/build/vfs_fonts";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Pie, PieChart, ResponsiveContainer } from "recharts";
import { db } from "../firebase";
import { CreateEvent } from "./CreateEvent";
pdfMake.vfs = pdfFonts;

type TaskType = {
  isComplete: boolean;
  isDeleted: boolean;
  taskOrg: string;
  startTime: number;
  endTime?: number;
  description?: string;
  id: string;
  taskDetail?: string;
  taskNote?: string;
};

type ClientType = {
  clientName: string;
  id: string;
  rate: string;
};

type CategoryType = {
  category: string;
  id: string;
  client?: string;
  name?: string;
};
export const Tasks = () => {
  const [taskForm] = Form.useForm();
  const [timeForm] = Form.useForm();
  const [descriptionForm] = Form.useForm();
  const [printForm] = Form.useForm();
  //const { width } = useRecoilValue(windowSize);
  const [activeId, setActiveId] = useState<string | null>(null);
  const [tasks, setTasks] = useState<Array<TaskType>>([]);
  const [clients, setClients] = useState<Array<ClientType>>([]);
  const [categories, setCategories] = useState<Array<CategoryType>>([]);
  const [isModal, setIsModal] = useState(false);
  const [graphData, setGraphData] = useState<Array<any>>([]);
  const [isEventModal, setIsEventModal] = useState(false);
  const [isStartTaskModal, setIsStartTaskModal] = useState(false);
  const [calendarMode, setCalendarMode] = useState<"day" | "week" | "month">("week");
  const [calendarDate, setCalendarDate] = useState(dayjs());
  const nav = useNavigate();

  const { RangePicker } = DatePicker;

  useEffect(() => {
    const q = query(
      collection(db, "tasks"),
      orderBy("startTime", "asc"),
      where("startTime", ">", new Date().getTime() - 1000 * 60 * 60 * 24 * 90),
    );
    const unsub = onSnapshot(q, (snapshot) => {
      setTasks([]);
      snapshot.forEach((snapDoc) => {
        setTasks((prev) => {
          return [...prev, { ...snapDoc.data(), id: snapDoc.id } as TaskType];
        });
      });
    });

    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "clients"), (snapshot) => {
      setClients([]);
      snapshot.forEach((snapDoc) => {
        setClients((prev) => {
          return [...prev, { ...snapDoc.data(), id: snapDoc.id } as ClientType];
        });
      });
    });

    return unsub;
  }, []);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, "categories"), (snapshot) => {
      setCategories([]);
      snapshot.forEach((snapDoc) => {
        setCategories((prev) => {
          return [
            ...prev,
            { ...snapDoc.data(), id: snapDoc.id } as CategoryType,
          ];
        });
      });
    });

    return unsub;
  }, []);

  const updateGraph = () => {
    const dates = printForm.getFieldValue("dates");
    const client = printForm.getFieldValue("client");

    if (dates && client) {
      const dateStart = dates[0].hour(0).minute(0).second(1).toDate().getTime();

      const dateEnd = dates[1]
        .hour(23)
        .minute(59)
        .second(59)
        .toDate()
        .getTime();

      const taskDetailSet = new Set(
        tasks
          .filter((task) => {
            if (!task.endTime) return false;
            if (
              task.startTime > dateStart &&
              task.endTime < dateEnd &&
              task.taskOrg === client
            ) {
              return true;
            } else {
              return false;
            }
          })
          .map((task) => task.taskDetail),
      );

      const data = Array.from(taskDetailSet).map((taskDetailMatch) => {
        let taskDetailTime = 0;

        tasks
          .filter((task) => {
            if (!task.endTime) return false;
            if (
              task.startTime > dateStart &&
              task.endTime < dateEnd &&
              task.taskOrg === client &&
              task.taskDetail === taskDetailMatch
            ) {
              return true;
            } else {
              return false;
            }
          })
          .forEach((task) => {
            taskDetailTime +=
              task.endTime && task.startTime
                ? task.endTime - task.startTime
                : 0;
          });

        return {
          name: taskDetailMatch,
          value: taskDetailTime,
        };
      });

      console.log(data);

      setGraphData([...data]);
    }
  };
  const openTask = () => {
    taskForm
      .validateFields()
      .then((values) => {
        const taskId = nanoid();
        setDoc(doc(db, `tasks/${taskId}`), {
          taskOrg: values.taskOrg,
          startTime: new Date().getTime(),
          isDeleted: false,
          isComplete: false,
        })
          .then(() => {
            message.success("Task started");
          })
          .catch((err) => {
            console.error(err);
          });
      })
      .catch((err) => console.error(err));
  };

  const closeTask = (id: string) => {
    descriptionForm
      .validateFields()
      .then((values) => {
        updateDoc(doc(db, `tasks/${id}`), {
          isComplete: true,
          endTime: new Date().getTime(),
          description: values.des,
          taskDetail: values.taskDetail,
        } as TaskType)
          .then(() => {
            message.success("Task completed.");
            setActiveId(null);
          })
          .catch((err) => {
            console.error(err);
          });
      })
      .catch((err) => {
        console.error(err);
        message.error("Failed to update");
      });
  };

  const printRecord = (
    content: any,
    dates: [number, number],
    client: string,
    amount: number,
    rate: number,
  ) => {
    const hours = amount / (1000 * 60 * 60);
    const hourlyRate = 100 * rate;
    const subtotal = hours * hourlyRate;
    const gstAmount = subtotal * 0.15;
    const total = subtotal + gstAmount;
    const now = new Date();
    const reference = `${now.getFullYear()}${(now.getMonth() + 1)
      .toString()
      .padStart(2, "0")}${now.getDate().toString().padStart(2, "0")}-${now.getHours()}${now
      .getMinutes()
      .toString()
      .padStart(2, "0")}`;

    const docDefinition: any = {
      pageSize: "A4",
      pageMargins: [42, 42, 42, 46],
      defaultStyle: {
        fontSize: 8.8,
        color: "#111827",
        lineHeight: 1.12,
      },
      content: [
        {
          columns: [
            {
              width: "*",
              stack: [
                { text: "SCRYPTWORX", style: "brand" },
                { text: "STUDIOS LIMITED", style: "brandSub" },
                { text: "Creative digital solutions", style: "mutedGold", margin: [0, 6, 0, 0] },
                { text: "GST 136-354-817", style: "muted" },
              ],
            },
            {
              width: 190,
              stack: [
                { text: "TIME INVOICE", style: "invoiceTitle", alignment: "right" },
                { text: reference, style: "invoiceNumber", alignment: "right" },
              ],
            },
          ],
        },
        {
          canvas: [
            { type: "line", x1: 0, y1: 18, x2: 511, y2: 18, lineWidth: 0.7, lineColor: "#9A6D26" },
            { type: "line", x1: 0, y1: 21, x2: 80, y2: 21, lineWidth: 2, lineColor: "#F6B34B" },
          ],
        },
        {
          columns: [
            {
              width: "*",
              stack: [
                { text: "BILL TO", style: "eyebrow" },
                { text: client, style: "clientName" },
                {
                  text: `Work period: ${new Date(dates[0]).toLocaleDateString("en-NZ")} - ${new Date(
                    dates[1],
                  ).toLocaleDateString("en-NZ")}`,
                  style: "muted",
                },
              ],
            },
            {
              width: 210,
              table: {
                widths: [82, "*"],
                body: [
                  [{ text: "Issue date", style: "metaLabel" }, { text: now.toLocaleDateString("en-NZ"), style: "metaValue" }],
                  [{ text: "Hours", style: "metaLabel" }, { text: hours.toFixed(2), style: "metaValue" }],
                  [{ text: "Amount due", style: "metaLabel" }, { text: `$${total.toFixed(2)}`, style: "metaTotal" }],
                ],
              },
              layout: {
                hLineWidth: () => 0,
                vLineWidth: () => 0,
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 2,
                paddingBottom: () => 2,
              },
            },
          ],
          margin: [0, 22, 0, 18],
        },
        {
          table: {
            headerRows: 1,
            widths: [48, 36, 36, 48, 82, "*"],
            body: [
              [
                { text: "Date", style: "tableHeader" },
                { text: "Start", style: "tableHeader" },
                { text: "End", style: "tableHeader" },
                { text: "Hours", style: "tableHeader", alignment: "right" },
                { text: "Category", style: "tableHeader" },
                { text: "Description", style: "tableHeader" },
              ],
              ...content,
            ],
          },
          layout: {
            hLineColor: (i: number) => (i === 1 ? "#F6B34B" : "#E5E7EB"),
            hLineWidth: (i: number) => (i === 0 ? 0 : i === 1 ? 0.8 : 0.45),
            vLineWidth: () => 0,
            paddingLeft: () => 0,
            paddingRight: () => 5,
            paddingTop: (i: number) => (i === 0 ? 5 : 7),
            paddingBottom: (i: number) => (i === 0 ? 5 : 7),
          },
          margin: [0, 0, 0, 18],
        },
        {
          columns: [
            {
              width: "*",
              stack: [
                { text: "PAYMENT DETAILS", style: "eyebrow" },
                { text: "Account name: Scryptworx Studios Limited", style: "small" },
                { text: "Account number: 12-3616-0018990-00", style: "small" },
                { text: `Reference: ${reference}`, style: "small" },
              ],
            },
            {
              width: 210,
              table: {
                widths: ["*", 82],
                body: [
                  [
                    { text: `${hours.toFixed(2)} hrs @ $${hourlyRate.toFixed(2)}`, style: "summaryLabel" },
                    { text: `$${subtotal.toFixed(2)}`, style: "summaryValue" },
                  ],
                  [{ text: "GST", style: "summaryLabel" }, { text: `$${gstAmount.toFixed(2)}`, style: "summaryValue" }],
                  [{ text: "Total", style: "summaryTotalLabel" }, { text: `$${total.toFixed(2)}`, style: "summaryTotalValue" }],
                ],
              },
              layout: {
                hLineColor: (i: number) => (i === 2 ? "#F6B34B" : "#E5E7EB"),
                hLineWidth: (i: number) => (i === 0 ? 0 : i === 2 ? 0.8 : 0.45),
                vLineWidth: () => 0,
                paddingLeft: () => 0,
                paddingRight: () => 0,
                paddingTop: () => 6,
                paddingBottom: () => 6,
              },
            },
          ],
        },
        { text: "Thank you for your business.", style: "thanks", margin: [0, 34, 0, 0] },
      ],
      footer: (currentPage: number, pageCount: number) => ({
        columns: [
          { text: "scryptworxstudios.com", style: "footer" },
          { text: `Time invoice ${reference} · Page ${currentPage} of ${pageCount}`, style: "footer", alignment: "right" },
        ],
        margin: [42, 0, 42, 20],
      }),
      info: {
        title: `Scryptworx Studios Time Invoice ${reference}`,
        author: "scryptworxstudios.com",
        subject: "Invoice for hours",
        keywords: "Invoice",
      },
      styles: {
        brand: { fontSize: 18, bold: true, characterSpacing: 1.2, color: "#1D1928" },
        brandSub: { fontSize: 8.5, bold: true, characterSpacing: 2, color: "#5C6B73" },
        invoiceTitle: { fontSize: 23, bold: true, characterSpacing: 1.2, color: "#1D1928" },
        invoiceNumber: { fontSize: 10, color: "#5C6B73", margin: [0, 4, 0, 0] },
        eyebrow: { fontSize: 7.5, bold: true, characterSpacing: 1.5, color: "#F6B34B", margin: [0, 0, 0, 5] },
        clientName: { fontSize: 13, bold: true, color: "#1D1928", margin: [0, 0, 0, 3] },
        muted: { fontSize: 8.5, color: "#5C6B73" },
        mutedGold: { fontSize: 8.5, color: "#9A6D26" },
        small: { fontSize: 8.5, color: "#374151", margin: [0, 1, 0, 1] },
        metaLabel: { fontSize: 8, color: "#6B7280" },
        metaValue: { fontSize: 9, bold: true, alignment: "right" },
        metaTotal: { fontSize: 12, bold: true, alignment: "right", color: "#1D1928" },
        tableHeader: { fontSize: 7.5, bold: true, characterSpacing: 1.1, color: "#5C6B73" },
        summaryLabel: { fontSize: 9, color: "#6B7280" },
        summaryValue: { fontSize: 9, alignment: "right" },
        summaryTotalLabel: { fontSize: 12, bold: true, color: "#1D1928" },
        summaryTotalValue: { fontSize: 12, bold: true, alignment: "right", color: "#1D1928" },
        thanks: { fontSize: 9, italics: true, color: "#6B7280" },
        footer: { fontSize: 7.5, color: "#9CA3AF" },
      },
    };
    pdfMake.createPdf(docDefinition).open();
  };

  const activeTaskCount = tasks.filter((task) => !task.isComplete).length;
  const completedTaskCount = tasks.filter((task) => task.isComplete).length;
  const totalTrackedHours = tasks
    .filter((task) => task.endTime)
    .reduce(
      (total, task) => total + ((task.endTime || 0) - task.startTime),
      0,
    ) / 1000 / 60 / 60;

  const formatDateTime = (time?: number) => {
    if (!time) return "--";
    return new Date(time).toLocaleString([], {
      day: "2-digit",
      month: "short",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const formatDuration = (start: number, end?: number) => {
    if (!end) return "Active";
    return `${((end - start) / 1000 / 60 / 60).toFixed(2)} hrs`;
  };

  const calendarTitle =
    calendarMode === "day"
      ? calendarDate.format("dddd, D MMMM YYYY")
      : calendarMode === "week"
        ? `${calendarDate.startOf("week").format("D MMM")} - ${calendarDate.endOf("week").format("D MMM YYYY")}`
        : calendarDate.format("MMMM YYYY");

  const moveCalendar = (direction: -1 | 1) => {
    const unit = calendarMode === "day" ? "day" : calendarMode === "week" ? "week" : "month";
    setCalendarDate((current) => current.add(direction, unit));
  };

  const taskEndTime = (task: TaskType) => task.endTime || new Date().getTime();

  const taskOverlapsDay = (task: TaskType, day: dayjs.Dayjs) => {
    const dayStart = day.startOf("day").valueOf();
    const dayEnd = day.endOf("day").valueOf();
    return task.startTime <= dayEnd && taskEndTime(task) >= dayStart;
  };

  const tasksForDay = (day: dayjs.Dayjs) =>
    tasks
      .filter((task) => !task.isDeleted)
      .filter((task) => taskOverlapsDay(task, day))
      .sort((a, b) => a.startTime - b.startTime);

  const CalendarTaskPill = ({ task, compact = false }: { task: TaskType; compact?: boolean }) => (
    <button
      type="button"
      onClick={() => {
        window.navigator.clipboard.writeText(task.id);
        message.info("Task ID copied");
      }}
      className={`h-full w-full overflow-hidden border px-2 py-1 text-left transition hover:border-[#F6B34B] ${
        task.isComplete
          ? "border-white/10 bg-[#1D1928]/80 text-gray-200"
          : "border-[#F6B34B]/50 bg-[#F6B34B]/15 text-white"
      }`}
    >
      <div className="truncate font-mono text-[10px] uppercase tracking-[0.14em] text-[#F6B34B]">
        {dayjs(task.startTime).format("HH:mm")} {task.endTime ? `- ${dayjs(task.endTime).format("HH:mm")}` : "active"}
      </div>
      <div className={`${compact ? "text-xs" : "text-sm"} truncate font-oswald uppercase tracking-tight`}>
        {task.taskOrg}
      </div>
      {!compact && task.taskDetail && <div className="truncate text-[11px] text-gray-300">{task.taskDetail}</div>}
    </button>
  );

  const TimeGrid = ({ days }: { days: dayjs.Dayjs[] }) => {
    const hours = Array.from({ length: 24 }, (_, index) => index);
    return (
      <div className="overflow-x-auto">
        <div className="grid min-w-[760px]" style={{ gridTemplateColumns: `64px repeat(${days.length}, minmax(150px, 1fr))` }}>
          <div className="border-b border-white/10" />
          {days.map((day) => (
            <div key={day.valueOf()} className="border-b border-l border-white/10 p-3 text-center">
              <div className="font-oswald text-lg uppercase text-white">{day.format("ddd")}</div>
              <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C6B73]">{day.format("D MMM")}</div>
            </div>
          ))}
          <div className="relative col-span-full grid" style={{ gridTemplateColumns: `64px repeat(${days.length}, minmax(150px, 1fr))`, height: 1152 }}>
            <div className="relative border-r border-white/10">
              {hours.map((hour) => (
                <div key={hour} className="absolute left-0 right-2 text-right font-mono text-[10px] text-[#5C6B73]" style={{ top: hour * 48 - 6 }}>
                  {`${hour.toString().padStart(2, "0")}:00`}
                </div>
              ))}
            </div>
            {days.map((day) => (
              <div key={day.valueOf()} className="relative border-r border-white/10">
                {hours.map((hour) => (
                  <div key={hour} className="absolute left-0 right-0 border-t border-white/5" style={{ top: hour * 48 }} />
                ))}
                {tasksForDay(day).map((task) => {
                  const dayStart = day.startOf("day").valueOf();
                  const dayEnd = day.endOf("day").valueOf();
                  const start = Math.max(task.startTime, dayStart);
                  const end = Math.min(taskEndTime(task), dayEnd);
                  const top = ((start - dayStart) / 1000 / 60 / 60) * 48;
                  const height = Math.max(((end - start) / 1000 / 60 / 60) * 48, 30);
                  return (
                    <div key={`${task.id}-${day.valueOf()}`} className="absolute left-1 right-1 z-10" style={{ top, height }}>
                      <CalendarTaskPill task={task} compact={height < 58} />
                    </div>
                  );
                })}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const MonthGrid = () => {
    const start = calendarDate.startOf("month").startOf("week");
    const days = Array.from({ length: 42 }, (_, index) => start.add(index, "day"));
    return (
      <div className="grid grid-cols-7 border-l border-t border-white/10">
        {days.map((day) => {
          const dayTasks = tasksForDay(day);
          const isCurrentMonth = day.month() === calendarDate.month();
          return (
            <div key={day.valueOf()} className={`min-h-[150px] border-b border-r border-white/10 p-2 ${isCurrentMonth ? "bg-black/10" : "bg-black/30 opacity-60"}`}>
              <div className="mb-2 flex items-center justify-between">
                <span className={`font-mono text-[10px] uppercase tracking-[0.2em] ${day.isSame(dayjs(), "day") ? "text-[#F6B34B]" : "text-[#5C6B73]"}`}>
                  {day.format("D")}
                </span>
                {dayTasks.length > 0 && <span className="text-[10px] text-[#F6B34B]">{dayTasks.length}</span>}
              </div>
              <div className="space-y-1">
                {dayTasks.slice(0, 4).map((task) => <CalendarTaskPill key={task.id} task={task} compact />)}
                {dayTasks.length > 4 && <div className="font-mono text-[10px] text-[#5C6B73]">+{dayTasks.length - 4} more</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const calendarDays =
    calendarMode === "day"
      ? [calendarDate]
      : Array.from({ length: 7 }, (_, index) => calendarDate.startOf("week").add(index, "day"));

  const calendarPeriodStart =
    calendarMode === "day"
      ? calendarDate.startOf("day").valueOf()
      : calendarMode === "week"
        ? calendarDate.startOf("week").valueOf()
        : calendarDate.startOf("month").valueOf();

  const calendarPeriodEnd =
    calendarMode === "day"
      ? calendarDate.endOf("day").valueOf()
      : calendarMode === "week"
        ? calendarDate.endOf("week").valueOf()
        : calendarDate.endOf("month").valueOf();

  const periodTasks = tasks.filter(
    (task) =>
      !task.isDeleted &&
      task.startTime <= calendarPeriodEnd &&
      taskEndTime(task) >= calendarPeriodStart,
  );

  const clientPeriodStats = Array.from(
    periodTasks.reduce((stats, task) => {
      const client = task.taskOrg || "Unassigned";
      const start = Math.max(task.startTime, calendarPeriodStart);
      const end = Math.min(taskEndTime(task), calendarPeriodEnd);
      const hours = Math.max(0, (end - start) / 1000 / 60 / 60);
      const existing = stats.get(client) || { client, hours: 0, taskCount: 0, activeCount: 0 };

      existing.hours += hours;
      existing.taskCount += 1;
      existing.activeCount += task.isComplete ? 0 : 1;
      stats.set(client, existing);
      return stats;
    }, new Map<string, { client: string; hours: number; taskCount: number; activeCount: number }>()),
  )
    .map(([, stat]) => stat)
    .sort((a, b) => b.hours - a.hours || a.client.localeCompare(b.client));

  const selectedPeriodTotalHours = clientPeriodStats.reduce((total, stat) => total + stat.hours, 0);

  return (
    <div className="min-h-screen w-full bg-[#1f1e23] text-white selection:bg-[#F6B34B] selection:text-[#1D1928]">
      <div className="pointer-events-none fixed inset-0 bg-[radial-gradient(circle_at_top,_rgba(246,179,75,0.08),_transparent_35%),linear-gradient(to_bottom,_rgba(0,0,0,0.15),_transparent)]" />

      <main className="relative z-10 mx-auto flex w-full max-w-7xl flex-col gap-8 px-6 py-10 md:py-16">
        <header className="border border-white/10 bg-[#1D1928]/50 p-6 backdrop-blur-sm md:p-10">
          <div className="flex flex-col gap-8 md:flex-row md:items-end md:justify-between">
            <div>
              <p className="font-mono text-xs uppercase tracking-[0.3em] text-[#5C6B73] animate-pulse">
                {"// OPS.TASKS :: TIME_TRACKING_ACTIVE"}
              </p>
              <h1 className="mt-5 font-oswald text-5xl uppercase leading-none tracking-tighter text-[#F6B34B] md:text-7xl">
                Tasks
              </h1>
              <p className="mt-5 max-w-xl border-l-2 border-[#F6B34B] pl-4 font-mono text-sm leading-relaxed text-gray-300 md:text-base">
                Track active client work, close completed tasks, and generate
                invoice-ready time reports.
                <span className="mt-2 block text-[#5C6B73]">
                  Last 90 days loaded from Firestore
                </span>
              </p>
            </div>

            <div className="flex flex-wrap gap-3">
              <button
                className="border border-[#5C6B73] px-5 py-3 font-mono text-xs uppercase tracking-widest text-[#5C6B73] transition-all hover:border-[#F6B34B] hover:text-[#F6B34B]"
                onClick={() => setIsEventModal(true)}
                type="button"
              >
                <CalendarOutlined /> Event
              </button>
              <button
                className="border border-[#5C6B73] px-5 py-3 font-mono text-xs uppercase tracking-widest text-[#5C6B73] transition-all hover:border-[#F6B34B] hover:text-[#F6B34B]"
                onClick={() => nav("/tools")}
                type="button"
              >
                Tools
              </button>
            </div>
          </div>
        </header>

        <section className="grid grid-cols-1 gap-4 md:grid-cols-3">
          <div className="border border-[#9A6D26]/20 bg-black/10 p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
              Active
            </p>
            <div className="mt-3 font-oswald text-4xl text-[#F6B34B]">
              {activeTaskCount}
            </div>
          </div>
          <div className="border border-[#9A6D26]/20 bg-black/10 p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
              Completed
            </p>
            <div className="mt-3 font-oswald text-4xl text-white">
              {completedTaskCount}
            </div>
          </div>
          <div className="border border-[#9A6D26]/20 bg-black/10 p-6">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
              Tracked Hours
            </p>
            <div className="mt-3 font-oswald text-4xl text-white">
              {totalTrackedHours.toFixed(2)}
            </div>
          </div>
        </section>

        <section className="border border-[#9A6D26]/20 bg-black/10">
          <div className="flex flex-col gap-4 border-b border-white/10 px-5 py-4 md:flex-row md:items-center md:justify-between md:px-8">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
                {"// calendar.view"}
              </p>
              <h2 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-white">
                Calendar
              </h2>
              <div className="mt-3 h-1 w-12 bg-[#F6B34B]" />
            </div>
            <div className="flex flex-wrap items-center gap-3">
              <Button icon={<LeftOutlined />} onClick={() => moveCalendar(-1)} />
              <div className="min-w-[220px] text-center font-mono text-xs uppercase tracking-[0.2em] text-[#F6B34B]">
                {calendarTitle}
              </div>
              <Button icon={<RightOutlined />} onClick={() => moveCalendar(1)} />
              <Button onClick={() => setCalendarDate(dayjs())}>Today</Button>
              <Segmented
                value={calendarMode}
                onChange={(value) => setCalendarMode(value as "day" | "week" | "month")}
                options={[
                  { label: "Day", value: "day" },
                  { label: "Week", value: "week" },
                  { label: "Month", value: "month" },
                ]}
              />
            </div>
          </div>
          <div className="p-5 md:p-8">
            {calendarMode === "month" ? <MonthGrid /> : <TimeGrid days={calendarDays} />}
          </div>
        </section>

        <section className="border border-[#9A6D26]/20 bg-black/10">
          <div className="flex flex-col gap-3 border-b border-white/10 px-5 py-4 md:flex-row md:items-end md:justify-between md:px-8">
            <div>
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
                {"// calendar.stats"}
              </p>
              <h2 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-white">
                Hours by Client
              </h2>
              <div className="mt-3 h-1 w-12 bg-[#F6B34B]" />
            </div>
            <div className="font-mono text-xs uppercase tracking-[0.2em] text-[#F6B34B]">
              {selectedPeriodTotalHours.toFixed(2)} hrs · {calendarTitle}
            </div>
          </div>
          <div className="grid grid-cols-1 gap-4 p-5 md:grid-cols-2 md:p-8 xl:grid-cols-4">
            {clientPeriodStats.length === 0 ? (
              <div className="border border-white/10 bg-white/[0.02] p-5 font-mono text-xs uppercase tracking-[0.25em] text-[#5C6B73] md:col-span-2 xl:col-span-4">
                No tracked tasks in this selected calendar period.
              </div>
            ) : (
              clientPeriodStats.map((stat) => {
                const percentage = selectedPeriodTotalHours > 0 ? (stat.hours / selectedPeriodTotalHours) * 100 : 0;
                return (
                  <article key={stat.client} className="border border-white/10 bg-white/[0.02] p-5">
                    <div className="mb-4 flex items-start justify-between gap-3">
                      <h3 className="font-oswald text-xl uppercase tracking-tight text-[#F6B34B]">
                        {stat.client}
                      </h3>
                      <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C6B73]">
                        {percentage.toFixed(0)}%
                      </span>
                    </div>
                    <div className="font-oswald text-4xl text-white">{stat.hours.toFixed(2)}</div>
                    <div className="mt-1 font-mono text-[10px] uppercase tracking-[0.2em] text-[#5C6B73]">
                      hours · {stat.taskCount} task{stat.taskCount === 1 ? "" : "s"}
                      {stat.activeCount > 0 ? ` · ${stat.activeCount} active` : ""}
                    </div>
                    <div className="mt-4 h-1.5 bg-[#1D1928]">
                      <div className="h-full bg-[#F6B34B]" style={{ width: `${Math.min(percentage, 100)}%` }} />
                    </div>
                  </article>
                );
              })
            )}
          </div>
        </section>

        <section className="border border-[#9A6D26]/20 bg-black/10">
          <div className="border-b border-white/10 px-5 py-4 md:px-8">
            <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
              {"// task.log"}
            </p>
            <h2 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-white">
              Recent Work Log
            </h2>
            <div className="mt-3 h-1 w-12 bg-[#F6B34B]" />
          </div>

          <div className="max-h-[58vh] space-y-4 overflow-y-auto p-5 md:p-8">
            {tasks
              .sort((itemA, itemB) => itemB.startTime - itemA.startTime)
              .map((item) => (
                <div
                  key={item.id}
                  className={`border p-5 transition-all duration-300 ${
                    item.isComplete
                      ? "border-white/10 bg-white/[0.02]"
                      : "border-[#F6B34B]/50 bg-[#F6B34B]/[0.04]"
                  }`}
                >
                  <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                    <div className="min-w-0 flex-1">
                      <button
                        className="font-oswald text-2xl uppercase tracking-tight text-left text-[#F6B34B] transition-colors hover:text-[#FAD48A]"
                        onClick={() => {
                          window.navigator.clipboard.writeText(item.id);
                          message.info("Copied!");
                        }}
                        type="button"
                      >
                        {item.taskOrg}
                      </button>

                      {item.taskDetail && (
                        <p className="mt-2 font-mono text-xs uppercase tracking-[0.2em] text-white">
                          {item.taskDetail}
                        </p>
                      )}
                      {item.description && (
                        <p className="mt-3 text-sm leading-relaxed text-gray-300">
                          {item.description}
                        </p>
                      )}
                    </div>

                    <div className="flex flex-col gap-3 text-left lg:min-w-[330px]">
                      <div className="grid grid-cols-1 gap-2 font-mono text-[10px] uppercase tracking-widest text-[#5C6B73] sm:grid-cols-3">
                        <span>Start: {formatDateTime(item.startTime)}</span>
                        <span>End: {formatDateTime(item.endTime)}</span>
                        <span className={item.isComplete ? "text-white" : "text-[#F6B34B]"}>
                          {formatDuration(item.startTime, item.endTime)}
                        </span>
                      </div>

                      <div className="flex justify-end gap-2">
                        {item.isComplete && (
                          <Button
                            type="text"
                            className="text-white"
                            icon={<EditOutlined style={{ color: "white" }} />}
                            onClick={() => {
                              if (activeId !== item.id) {
                                setActiveId(item.id);
                                const thisTask = tasks.filter(
                                  (t) => t.id === item.id,
                                )[0];

                                timeForm.setFieldsValue({
                                  start: dayjs(thisTask.startTime),
                                  end: dayjs(thisTask.endTime),
                                });

                                setIsModal(true);
                              } else {
                                setActiveId(null);
                                setIsModal(false);
                              }
                            }}
                          />
                        )}
                        {!item.isComplete && activeId !== item.id && (
                          <Button
                            type="primary"
                            icon={<CheckSquareOutlined />}
                            onClick={() => setActiveId(item.id)}
                          >
                            Complete
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>

                  {activeId === item.id && !isModal && (
                    <div className="mt-5 border-t border-white/10 pt-5">
                      <div className="mb-4 flex items-center justify-between gap-3">
                        <p className="font-mono text-xs uppercase tracking-[0.2em] text-[#F6B34B]">
                          Complete this task?
                        </p>
                        <Popconfirm
                          title="Finish task?"
                          onConfirm={() => closeTask(item.id)}
                        >
                          <Button>Save Complete</Button>
                        </Popconfirm>
                      </div>
                      <Form form={descriptionForm} layout="vertical">
                        <Form.Item
                          name="taskDetail"
                          rules={[{ required: true, message: "Required" }]}
                        >
                          <Select placeholder="Category">
                            {categories
                              .filter((cat) => item.taskOrg === (cat.client || cat.name))
                              .map((cat, idx) => (
                                <Select.Option key={idx} value={cat.category}>
                                  {cat.category}
                                </Select.Option>
                              ))}
                          </Select>
                        </Form.Item>
                        <Form.Item
                          name="des"
                          rules={[{ required: true, message: "Required" }]}
                        >
                          <Input.TextArea placeholder="Description" rows={4} />
                        </Form.Item>
                      </Form>
                    </div>
                  )}
                </div>
              ))}
          </div>
        </section>

        <section className="grid grid-cols-1 gap-8 lg:grid-cols-[420px_1fr]">
          <div className="border border-[#9A6D26]/20 bg-black/10">
            <div className="border-b border-white/10 px-5 py-4 md:px-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
                {"// invoice.print"}
              </p>
              <h2 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-white">
                Print Invoice
              </h2>
              <div className="mt-3 h-1 w-12 bg-[#F6B34B]" />
            </div>
            <div className="p-5 md:p-8">
              <Form form={printForm} layout="vertical">
                <Form.Item name="dates" label={<span className="font-mono uppercase text-[#5C6B73]">Date Range</span>}>
                  <RangePicker />
                </Form.Item>
                <Form.Item name="client" label={<span className="font-mono uppercase text-[#5C6B73]">Client</span>}>
                  <Select placeholder="Select client">
                    {clients.map((item, idx) => (
                      <Select.Option key={idx} value={item.clientName}>
                        {`${item.clientName} - ${item.rate}`}
                      </Select.Option>
                    ))}
                  </Select>
                </Form.Item>
                <Form.Item name="rate" label={<span className="font-mono uppercase text-[#5C6B73]">Rate</span>}>
                  <InputNumber className="w-full" />
                </Form.Item>
                <div className="flex flex-wrap gap-3">
                  <Button
                    type="primary"
                    icon={<PrinterOutlined />}
                    onClick={() => {
                      let amount = 0;
                      printForm.validateFields().then(
                        (values: {
                          dates: Array<Moment>;
                          client: string;
                          rate: number;
                        }) => {
                          const dateStart = values.dates[0]
                            .hour(0)
                            .minute(0)
                            .second(1)
                            .toDate()
                            .getTime();
                          const dateEnd = values.dates[1]
                            .hour(23)
                            .minute(59)
                            .second(59)
                            .toDate()
                            .getTime();
                          const content = tasks
                            .filter((task) => {
                              if (!task.endTime) return false;
                              return (
                                task.startTime > dateStart &&
                                task.endTime < dateEnd &&
                                task.taskOrg === values.client
                              );
                            })
                            .sort((a, b) => a.startTime - b.startTime)
                            .map((task) => {
                              const end = task.endTime || 0;

                              amount = amount + (end - task.startTime);

                              return [
                                {
                                  text: `${new Date(
                                    task.startTime,
                                  ).toLocaleDateString()}`,
                                  style: "small",
                                  borderColor: [
                                    "#ffffff",
                                    "#ffffff",
                                    "#ffffff",
                                    "#ffffff",
                                  ],
                                },
                                {
                                  text: `${new Date(task.startTime).getHours()}:${
                                    new Date(task.startTime).getMinutes() < 10
                                      ? "0" + new Date(task.startTime).getMinutes()
                                      : new Date(task.startTime).getMinutes()
                                  }`,
                                  style: "small",
                                  borderColor: [
                                    "#ffffff",
                                    "#ffffff",
                                    "#ffffff",
                                    "#ffffff",
                                  ],
                                },
                                {
                                  text: `${new Date(end).getHours()}:${
                                    new Date(end).getMinutes() < 10
                                      ? "0" + new Date(end).getMinutes()
                                      : new Date(end).getMinutes()
                                  }`,
                                  style: "small",
                                  borderColor: [
                                    "#ffffff",
                                    "#ffffff",
                                    "#ffffff",
                                    "#ffffff",
                                  ],
                                },
                                {
                                  text: `${(
                                    (end - task.startTime) /
                                    1000 /
                                    60 /
                                    60
                                  ).toPrecision(2)} hrs`,
                                  style: "small",
                                  borderColor: [
                                    "#ffffff",
                                    "#ffffff",
                                    "#ffffff",
                                    "#ffffff",
                                  ],
                                },
                                {
                                  text: `${task.taskDetail ? task.taskDetail : ""}`,
                                  style: "small",
                                  borderColor: [
                                    "#ffffff",
                                    "#ffffff",
                                    "#ffffff",
                                    "#ffffff",
                                  ],
                                },
                                {
                                  text: `${task.description}`,
                                  style: "small",
                                  borderColor: [
                                    "#ffffff",
                                    "#ffffff",
                                    "#ffffff",
                                    "#ffffff",
                                  ],
                                },
                              ];
                            });

                          printRecord(
                            content,
                            [dateStart, dateEnd],
                            values.client,
                            amount,
                            values.rate,
                          );
                        },
                      );
                    }}
                  >
                    Print
                  </Button>
                  <Button icon={<PieChartOutlined />} onClick={() => updateGraph()}>
                    Breakdown
                  </Button>
                </div>
              </Form>
            </div>
          </div>

          <div className="border border-[#9A6D26]/20 bg-black/10">
            <div className="border-b border-white/10 px-5 py-4 md:px-8">
              <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
                {"// analytics.time"}
              </p>
              <h2 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-white">
                Time Breakdown
              </h2>
              <div className="mt-3 h-1 w-12 bg-[#F6B34B]" />
            </div>
            <div className="h-[420px] p-5 md:p-8">
              {graphData.length > 0 ? (
                <ResponsiveContainer width="100%" height="100%">
                  <PieChart width={400} height={400} title="Time Spent">
                    <Pie
                      data={[...graphData]}
                      dataKey="value"
                      cx="50%"
                      cy="50%"
                      outerRadius={150}
                      fill="#F6B34B"
                      label={({ name, value }) =>
                        `${name}: ${(value / 1000 / 60 / 60).toFixed(2)} hrs`
                      }
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex h-full items-center justify-center text-center font-mono text-xs uppercase tracking-[0.25em] text-[#5C6B73]">
                  Select date range/client and click Breakdown
                </div>
              )}
            </div>
          </div>
        </section>
      </main>

      <Modal
        open={isStartTaskModal}
        footer={null}
        onCancel={() => setIsStartTaskModal(false)}
      >
        <div className="mb-4">
          <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-[#5C6B73]">
            {"// task.new"}
          </p>
          <h2 className="mt-2 font-oswald text-2xl uppercase tracking-tight text-[#1f1e23]">
            Start Task
          </h2>
        </div>
        <Form form={taskForm} layout="vertical">
          <Form.Item
            name="taskOrg"
            label={<span className="font-mono uppercase text-[#5C6B73]">Client</span>}
            rules={[{ required: true, message: "Select a client" }]}
          >
            <Select placeholder="Select client">
              {clients.map((item, idx) => (
                <Select.Option key={idx} value={item.clientName}>
                  {item.clientName}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
          <Popconfirm
            title="Start Task?"
            onConfirm={() => {
              openTask();
              setIsStartTaskModal(false);
            }}
          >
            <Button type="primary" icon={<PlusOutlined />}>
              Start Task
            </Button>
          </Popconfirm>
        </Form>
      </Modal>
      <Modal
        open={isModal}
        footer={null}
        onCancel={() => {
          setIsModal(false);
          setActiveId(null);
        }}
      >
        <div className="mb-4 font-oswald text-2xl uppercase text-[#1f1e23]">
          Edit Time
        </div>
        <Form form={timeForm} layout="vertical">
          <Form.Item name="start" label="Start">
            <DatePicker placeholder="Start Date" showTime />
          </Form.Item>
          <Form.Item name="end" label="End">
            <DatePicker showTime placeholder="End Date" />
          </Form.Item>
        </Form>
        <Popconfirm
          title="Save new times"
          onConfirm={() => {
            timeForm
              .validateFields()
              .then((values) => {
                updateDoc(doc(db, `tasks/${activeId}`), {
                  endTime: values.end.toDate().getTime(),
                  startTime: values.start.toDate().getTime(),
                } as TaskType)
                  .then(() => {
                    message.success("Task updated.");
                    setActiveId(null);
                    setIsModal(false);
                  })
                  .catch((err) => {
                    message.error("Failed to update task.");
                    console.error(err);
                  });
              })
              .catch((err) => console.error(err));
          }}
        >
          <Button type="primary" icon={<SaveOutlined />}>
            Save
          </Button>
        </Popconfirm>
      </Modal>
      <Modal
        open={isEventModal}
        footer={null}
        onCancel={() => setIsEventModal(false)}
      >
        <CreateEvent />
      </Modal>
      <FloatButton.Group shape="circle" style={{ right: 24, bottom: 24 }}>
        <FloatButton
          type="primary"
          icon={<PlusOutlined />}
          tooltip="Start task"
          onClick={() => {
            if (activeTaskCount > 0) {
              message.info("Complete the active task before starting another.");
              return;
            }
            setIsStartTaskModal(true);
          }}
        />
        <FloatButton
          icon={<HomeOutlined />}
          tooltip="Tools"
          onClick={() => {
            nav("/tools");
          }}
        />
      </FloatButton.Group>
    </div>
  );
};
